import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import { importFilesSSE, fetchFolderTree, FolderTreeItem, fetchTags, assignTag } from '../../store/actions/FolderAction';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { FolderProps, TagItem } from './documents/types';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { Collaborator } from '../shared/CollaboratorSelector';
import Button from '../shared/TailwindComponents/Button';
import { FormField, FormSchema, FormValidator } from '../../utils/validation/form-schema';
import { sanitizeString } from '../../utils/validation';
import { FILE_IMPORT_RESET } from '../../store/types';
import { PiCheckCircleLight, PiXCircleLight, PiSpinnerLight, PiWarningCircleLight, PiFileLight, PiArrowLeftLight } from 'react-icons/pi';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, importFilesFormTourSteps } from '../shared/FeatureTour';

interface CreateFolderWithFilesFormProps {
  onClose: () => void;
  existingFolders: FolderProps[];
  selectedFolderItem?: any;
  onRefresh?: (createdFolderId?: string) => void | Promise<void>;
}

const CreateFolderWithFilesForm: React.FC<CreateFolderWithFilesFormProps> = ({
  onClose,
  existingFolders,
  selectedFolderItem,
  onRefresh
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => (state as any).auth?.user);

  const { isConnecting, connected, steps, ok, summary, complete } = useSelector(
    (state: RootState) => state.folders
  );

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [selectedParentFolderID, setSelectedParentFolderID] = useState<string>(selectedFolderItem?.FolderID || "");
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toggle, setToggle] = useState<'existing' | 'new'>('existing');
  const [result, setResult] = useState<any>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [recievedFolderID, setRecievedFolderID] = useState<string | null>(null);
  const [folderTree, setFolderTree] = useState<FolderTreeItem[]>([]);
  const [adminUuid, setAdminUuid] = useState<string | undefined>(undefined);
  const [orgTags, setOrgTags] = useState<TagItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'import-files-form',
    steps: importFilesFormTourSteps,
    autoStartDelay: 600,
  });

  // Reset SSE state on mount to clear stale data from previous imports
  useEffect(() => {
    dispatch({ type: FILE_IMPORT_RESET });
  }, [dispatch]);

  // Fetch folder tree and tags on mount
  useEffect(() => {
    setIsLoadingData(true);
    Promise.all([
      (dispatch(fetchFolderTree()) as any).catch(() => []),
      (dispatch(fetchTags()) as any).catch(() => []),
    ]).then(([tree, tags]) => {
      setFolderTree(tree || []);
      setOrgTags(tags || []);
      setIsLoadingData(false);
    });
  }, [dispatch]);

  // Build hierarchical dropdown options from folder tree
  const folderOptions: DropdownOption[] = useMemo(() => {
    const options = folderTree.map((f) => ({
      id: f.id,
      value: f.id,
      label: f.label,
      parent_id: f.parent_id,
    }));

    // Add the currently viewed folder if provided and not already in tree
    if (selectedFolderItem && !options.find(o => o.id === selectedFolderItem.FolderID)) {
      options.push({
        id: selectedFolderItem.FolderID,
        value: selectedFolderItem.FolderID,
        label: selectedFolderItem.FolderName,
        parent_id: null,
      });
    }

    return options;
  }, [folderTree, selectedFolderItem]);

  useEffect(() => {
    setResult(summary);
    if (summary && summary.folderId) {
      setRecievedFolderID(summary.folderId);
      // Assign selected tags to the newly created folder
      if (selectedTagIds.length > 0) {
        (async () => {
          for (const tagId of selectedTagIds) {
            await dispatch(assignTag(tagId, 'add', undefined, summary.folderId) as any);
          }
        })();
      }
    }
  }, [summary, dispatch, selectedTagIds]);

  // Set collaborators from tree data when parent folder changes
  useEffect(() => {
    if (selectedParentFolderID) {
      const selectedFolder = folderTree.find(f => f.id === selectedParentFolderID);
      if (selectedFolder && selectedFolder.collaborators) {
        // Find admin collaborator
        const admin = selectedFolder.collaborators.find((c: any) => c.access === 'admin');
        setAdminUuid(admin?.user_uuid);

        // Filter out current user, map to collaborator format
        const filtered = selectedFolder.collaborators
          .filter((c: any) => c.user_uuid !== currentUser?.uuid)
          .map((c: any) => ({
            collaborator_uuid: c.user_uuid,
            email: c.email,
            name: c.name,
            access: c.access || 'view',
            role: c.role,
            initials: c.initials,
            avatar_url: c.avatar_url
          }));
        setCollaborators(filtered);
      } else {
        setCollaborators([]);
        setAdminUuid(undefined);
      }
    } else {
      setCollaborators([]);
      setAdminUuid(undefined);
    }
  }, [selectedParentFolderID, folderTree, currentUser?.uuid]);

  // Clear collaborators when toggle switches to 'new'
  useEffect(() => {
    if (toggle === 'new') {
      setCollaborators([]);
      setSelectedParentFolderID('');
      setAdminUuid(undefined);
    }
  }, [toggle]);

  const handleClose = async () => {
    // Navigate to the target folder to show uploaded files
    if (recievedFolderID) {
      // Small delay to ensure folder data is ready
      await new Promise(resolve => setTimeout(resolve, 300));

      const currentPath = location.pathname.replace(/\/$/, '');
      const targetPath = `/files/${recievedFolderID}`;

      // If we're already viewing this folder, force a refresh via onRefresh
      if (currentPath.endsWith(recievedFolderID)) {
        if (onRefresh) await onRefresh(recievedFolderID);
        dispatch({ type: FILE_IMPORT_RESET });
        onClose();
      } else {
        dispatch({ type: FILE_IMPORT_RESET });
        history.push(targetPath);
      }
    } else {
      if (onRefresh) await onRefresh();
      dispatch({ type: FILE_IMPORT_RESET });
      onClose();
    }
  };

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        fields: [
          {
            name: 'folderMode',
            type: 'custom',
            label: t('Folder'),
            customComponent: ({ value, onChange }) => (
              <div className="gap-4">
                <label className="block text-xs font-light text-gray-700 dark:text-gray-300 mb-2">
                  {t("Folder")}
                </label>
                <div className="flex w-fit border border-gray-300 dark:border-gray-600 rounded overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setToggle('existing');
                      onChange('existing');
                    }}
                    className={`px-2.5 py-1.5 text-xs font-light transition-colors ${toggle === 'existing'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    {t("Use existing")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setToggle('new');
                      onChange('new');
                    }}
                    className={`px-2.5 py-1.5 text-xs font-light transition-colors border-l border-gray-300 dark:border-gray-600 ${toggle === 'new'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    {t("Create new")}
                  </button>
                </div>
              </div>
            ),
            gridSize: 'full'
          } as BaseFormFieldConfig,

          {
            name: 'parentFolder',
            type: 'dropdown',
            label: toggle === 'new' ? t('Parent Folder') : t('Path'),
            options: folderOptions,
            placeholder: t('Choose a folder'),
            description: toggle === 'new' ? t('(optional)') : undefined,
            customProps: { fullWidth: true, hierarchical: true },
            gridSize: 'full',
            onChange: (value: string) => {
              if (toggle === 'existing') {
                setSelectedParentFolderID(value);
              }
            }
          } as BaseFormFieldConfig,

          ...(toggle === 'new' ? [{
            name: 'folderName',
            type: 'input',
            label: t('Folder Name'),
            placeholder: t('e.g., Contracts 2025'),
            required: true,
            gridSize: 'full'
          }] : []) as BaseFormFieldConfig[]
        ]
      },
      ...(orgTags.length > 0 ? [{
        fields: [
          {
            name: 'tags',
            type: 'custom',
            label: t('Tags'),
            gridSize: 'full',
            customComponent: ({ value, onChange }: any) => {
              const selected: number[] = value || [];
              return (
                <div>
                  <label className="block text-xs font-light text-gray-700 dark:text-gray-300 mb-2">
                    {t('Tags (optional)')}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {orgTags.map(tag => {
                      const isSelected = selected.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            const next = isSelected
                              ? selected.filter((id: number) => id !== tag.id)
                              : [...selected, tag.id];
                            onChange(next);
                            setSelectedTagIds(next);
                          }}
                          className={`transition-all ${isSelected ? 'ring-2 ring-primary-400 ring-offset-1 dark:ring-offset-gray-900 rounded-full' : ''}`}
                        >
                          <Badge color={(tag.color as BadgeColor) || 'gray'} className="text-xs cursor-pointer">
                            {tag.name}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }
          } as BaseFormFieldConfig
        ]
      }] : []),
      {
        title: t('Share with team members'),
        description: toggle === 'existing' && selectedParentFolderID
          ? t('People from parent folder are pre-selected. Admin access cannot be removed.')
          : t('Add people who can access this folder'),
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Access'),
            gridSize: 'full',
            customProps: { adminUuid }
          } as BaseFormFieldConfig
        ]
      },
      {
        fields: [
          {
            name: 'files',
            type: 'fileUploader',
            label: t('Import Files'),
            gridSize: 'full',
            required: true,
          } as BaseFormFieldConfig
        ]
      }
    ];
  }, [toggle, folderOptions, t, selectedParentFolderID, adminUuid, orgTags]);

  const schema = useMemo(() => {
    const schemaFields: FormField[] = [
      new FormField('folderMode', 'text', false, 'Folder Mode', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('parentFolder', 'text', false, 'Parent Folder', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('folderName', 'text', toggle === 'new', t('Folder Name'), {
        customValidator: (value: any) => {
          if (toggle === 'new' && (!value || !String(value).trim())) {
            return { isValid: false, error: t('Folder name is required') };
          }
          return { isValid: true, sanitized: value };
        },
      }),
      new FormField('tags', 'text', false, 'Tags', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('collaborators', 'text', false, 'Access', {
        customValidator: () => ({ isValid: true }),
      }),
      new FormField('files', 'file', true, t('Files'), {
        customValidator: (value: any) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Please add at least one file') };
          }
          return { isValid: true, sanitized: value };
        },
      }),
    ];

    return new FormSchema('CreateFolderWithFiles', schemaFields);
  }, [toggle, t]);

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      setIsSubmitting(true);

      const sanitizedFolderName = toggle === 'new' && formData.folderName
        ? sanitizeString(formData.folderName, 255, false)
        : '';

      const validCollaborators = (formData.collaborators || [])
        .map((c: any) => ({
          user_uuid: c.collaborator_uuid || c.user_id,
          access: c.access
        }))
        .filter((c: any) => c.user_uuid);

      await dispatch(
        importFilesSSE(
          {
            FolderID: toggle === 'existing' ? formData.parentFolder : undefined,
            FolderName: sanitizedFolderName,
            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined
          },
          formData.files || []
        )
      );

      // Note: isSubmitting will be set to false in the SSE Redux action when upload completes
    } catch (error: any) {
      console.error('[CreateFolderWithFilesForm] Submission error:', error);
      setIsSubmitting(false);
    }
  };

  // Auto-navigate when SSE completes successfully
  useEffect(() => {
    if (complete && isSubmitting) {
      setIsSubmitting(false);

      // Auto-navigate on success after a short delay for visual feedback
      if (ok && recievedFolderID) {
        const timer = setTimeout(() => {
          handleClose();
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [complete, isSubmitting, ok, recievedFolderID]);

  // Derive upload state for inline display
  const isUploading = isSubmitting && (isConnecting || connected || steps?.length > 0);
  const uploadComplete = complete && !isSubmitting;
  const completedCount = steps?.filter((s: any) => s.status === 'Completed').length || 0;
  const failedCount = steps?.filter((s: any) => s.status === 'Failed').length || 0;
  const totalCount = steps?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Upload progress inline component
  const renderUploadProgress = () => {
    if (!isUploading && !uploadComplete) return null;

    return (
      <div className="flex flex-col justify-between h-full min-h-[70vh] space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          {isUploading ? (
            <PiSpinnerLight className="w-5 h-5 text-primary-500 animate-spin flex-shrink-0" />
          ) : result?.ok ? (
            <PiCheckCircleLight className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <PiWarningCircleLight className="w-5 h-5 text-red-500 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {isUploading
                ? isConnecting
                  ? t('Connecting...')
                  : `${t('Uploading')} ${completedCount}/${totalCount} ${t('files')}`
                : result?.ok
                  ? failedCount > 0
                    ? t('Import completed with errors')
                    : t('Files imported successfully')
                  : t('Import failed')}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                uploadComplete && !result?.ok ? 'bg-red-500' : 'bg-primary-500'
              }`}
              style={{
                width: `${uploadComplete ? 100 : progressPercent}%`,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>
        )}

        {/* File list */}
        {steps && steps.length > 0 && (
          <div className="flex-1 space-y-1.5 overflow-y-auto">
            {steps.map((step: any, idx: number) => (
              <div
                key={idx}
                className="flex items-center gap-2.5 py-1.5 px-2 rounded text-sm"
              >
                <div className="flex-shrink-0">
                  {step.status === 'Completed' ? (
                    <PiCheckCircleLight className="w-4 h-4 text-green-600" />
                  ) : step.status === 'Failed' ? (
                    <PiXCircleLight className="w-4 h-4 text-red-500" />
                  ) : step.status === 'In Progress' ? (
                    <PiSpinnerLight className="w-4 h-4 text-primary-500 animate-spin" />
                  ) : (
                    <PiFileLight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <span className={`flex-1 truncate ${
                  step.status === 'Failed' ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {step.label || `File ${idx + 1}`}
                </span>
                {step.status === 'Failed' && step.messages?.length > 0 && (
                  <span className="text-xs text-red-500 flex-shrink-0">
                    {step.messages[step.messages.length - 1]}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Result summary */}
        {uploadComplete && result && (
          <div className={`flex items-center justify-between px-3 py-2 rounded-md text-xs ${
            result.ok
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          }`}>
            <span>{result.message}</span>
          </div>
        )}

        {/* Action buttons */}
        {uploadComplete && (
          <div className="flex justify-end gap-2 pt-1 mt-auto">
            {!result?.ok && (
              <Button
                type="button"
                variant="neutral"
                size="small"
                onClick={() => {
                  dispatch({ type: FILE_IMPORT_RESET });
                  setResult(null);
                }}
              >
                <PiArrowLeftLight className="w-3.5 h-3.5 mr-1" />
                {t('Try Again')}
              </Button>
            )}
            <Button
              type="button"
              variant={result?.ok ? 'primary' : 'neutral'}
              size="small"
              onClick={handleClose}
            >
              {result?.ok ? t('Go to Folder') : t('Close')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Build form sections — inject upload progress as a custom field when uploading
  const activeSections: BaseFormSectionConfig[] = useMemo(() => {
    if (isUploading || uploadComplete) {
      return [{
        fields: [{
          name: '_uploadProgress',
          type: 'custom',
          label: '',
          customComponent: () => renderUploadProgress(),
          gridSize: 'full'
        } as BaseFormFieldConfig]
      }];
    }
    return formSections;
  }, [isUploading, uploadComplete, formSections, steps, result, isConnecting, connected, completedCount, totalCount]);

  if (isLoadingData) {
    return <FormSkeleton />;
  }

  return (
    <div data-tour-id="import-files-form">
      <BaseForm
        title={isUploading ? t('Importing Files') : uploadComplete ? (result?.ok ? t('Import Complete') : t('Import Failed')) : t('Create Folder & Import Files')}
        description={isUploading ? t('Please wait while your files are being uploaded.') : uploadComplete ? '' : t('Set up a new folder and upload files, or upload to an existing folder.')}
        sections={activeSections}
        schema={schema}
        initialValues={{
          folderMode: 'existing',
          parentFolder: (selectedFolderItem?.FolderID) || '',
          folderName: '',
          tags: [],
          collaborators: collaborators,
          files: []
        }}
        onSubmit={handleFormSubmit}
        onCancel={isUploading || uploadComplete ? undefined : onClose}
        submitLabel={t('Import Files')}
        cancelLabel={t('Cancel')}
        isSubmitting={isSubmitting}
        hideActions={isUploading || uploadComplete}
        hideLoadingOverlay={isUploading || uploadComplete}
        maxWidth="max-w-2xl"
        loadingText={t('Importing files...')}
      />
      <FeatureTour
        steps={importFilesFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default CreateFolderWithFilesForm;
